import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import {
  getPublicEventTicketDoorCheckInPage,
  submitPublicEventTicketDoorCheckIn,
} from '../../../services/eventApi';

/**
 * Buyer self check-in after scanning the door QR at the entrance.
 */
export default function PublicEventTicketDoorCheckInPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageData, setPageData] = useState(null);
  const [ticketCode, setTicketCode] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getPublicEventTicketDoorCheckInPage(token);
        if (!cancelled) setPageData(res.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Door check-in link is not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = String(ticketCode || '').trim().toUpperCase();
    const holderEmail = String(email || '').trim().toLowerCase();
    if (!code || !holderEmail) {
      toast.error('Enter your ticket code and email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitPublicEventTicketDoorCheckIn(token, {
        ticketCode: code,
        email: holderEmail,
      });
      const data = res.data?.data || {};
      setSuccess({
        ticketCode: data.ticket?.code || code,
        holderName: data.holderName || data.ticket?.holderName || '',
        eventDay: data.eventDay,
      });
      toast.success('You are checked in');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Check-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Check-in unavailable</h1>
          <p className="mt-2 text-sm text-gray-600">{error || 'Invalid or expired link'}</p>
        </div>
      </div>
    );
  }

  const event = pageData.event || {};

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white px-4">
        <div className="max-w-md w-full bg-white border border-green-200 rounded-xl p-6 shadow-sm text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xl font-bold">
            ✓
          </div>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">You&apos;re checked in</h1>
          <p className="mt-2 text-sm text-gray-600">{event.name}</p>
          {success.holderName ? (
            <p className="mt-3 text-base font-medium text-gray-900">{success.holderName}</p>
          ) : null}
          <p className="mt-1 font-mono text-indigo-700">{success.ticketCode}</p>
          {success.eventDay ? (
            <p className="mt-2 text-xs text-gray-500">Event day {success.eventDay}</p>
          ) : null}
          <p className="mt-4 text-sm text-gray-600">Show this screen at the door if asked.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/80 to-white">
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="text-center mb-6">
          <p className="text-xs font-medium uppercase tracking-wide text-indigo-600">Event check-in</p>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">{event.name || 'Event'}</h1>
          {event.venue ? <p className="mt-1 text-sm text-gray-500">{event.venue}</p> : null}
          {pageData.eventDay ? (
            <p className="mt-2 text-xs text-indigo-700 font-medium">Day {pageData.eventDay}</p>
          ) : null}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4"
        >
          <p className="text-sm text-gray-600">
            Enter the ticket code from your confirmation email and the email address the ticket was issued to.
          </p>

          <label className="block text-xs font-medium text-gray-700">
            Ticket code
            <TextInput
              variant="input"
              type="text"
              value={ticketCode}
              onChange={(e) => setTicketCode(e.target.value.toUpperCase())}
              placeholder="EVT-TKT-000001"
              className="mt-1 font-mono text-sm"
              autoComplete="off"
            />
          </label>

          <label className="block text-xs font-medium text-gray-700">
            Email on ticket
            <TextInput
              variant="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1 text-sm"
              autoComplete="email"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Checking in…' : 'Check in'}
          </button>
        </form>
      </div>
    </div>
  );
}
